import type { ProgressChart } from "@/domains/progress/models/types";
import { formatProgressScore } from "@/domains/progress/utils/chart-copy";

const MS_PER_DAY = 86_400_000;

/** `day` is days since the baseline: fixed calendar offsets, never evenly spaced slots. */
const AXIS_TICKS = [
  { day: 0, label: "Start", widthPx: 34 },
  { day: 14, label: "Wk 2", widthPx: 35 },
  { day: 42, label: "Wk 6", widthPx: 36 },
  { day: 70, label: "Wk 10", widthPx: 43 },
  { day: 91, label: "3 mo", widthPx: 33 },
] as const;

const AXIS_MIN_SPAN_DAYS = 91;

/** Room at the left and right edges for the first and last value label. */
const X_INSET = 4;

/** Room above the scale ceiling for a value label and the "You Are Here" pill. */
const Y_INSET_TOP = 26;

/** Room below the scale floor for the skipped-check-in markers. */
const Y_INSET_BOTTOM = 10;

const TICK_LABEL_PADDING_PX = 6;

/** Damped Catmull-Rom; control points are also clamped, damping alone overshoots. */
const SMOOTHING = 0.8;

/** A `text-sm` semibold digit and decimal point, measured the way AXIS_TICKS widths were. */
const VALUE_LABEL_DIGIT_PX = 8;
const VALUE_LABEL_POINT_PX = 4;
const VALUE_LABEL_PADDING_PX = 6;

/** The `text-sm` line box a value label occupies, and the `mb-1.5` that sets it off its dot. */
const VALUE_LABEL_HEIGHT_PX = 20;
const VALUE_LABEL_GAP_PX = 6;

/** The box a dot occupies: `size-2` for a reading, `size-4` for the latest one. */
const DOT_BOX_PX = 8;
const LATEST_DOT_BOX_PX = 16;

/** The "You Are Here" pill: a `text-sm` line inside `px-2.5 py-1`. */
const PILL_HEIGHT_PX = 28;
const PILL_WIDTH_PX = 110;

/** `mt-2` when the pill hangs below its dot, `mb-8` when it sits above. */
const PILL_BELOW_GAP_PX = 8;
const PILL_ABOVE_GAP_PX = 32;

/** The pill would run off either edge if it were centred on an early or a late point. */
const PILL_LEFT_ANCHOR_X = 20;
const PILL_RIGHT_ANCHOR_X = 55;

/** Above this the dot is too close to the scale floor for the pill to sit underneath. */
const PILL_BELOW_MAX_Y = 70;

/**
 * Tried in order, so a label only leaves its usual spot above the dot when that spot is taken.
 * The two centred spots come first: a label beside a dot sits in a neighbor's column, and the
 * column is what tells a patient which reading the number belongs to.
 */
const VALUE_LABEL_PLACEMENTS = ["above", "below", "right", "left"] as const;

export type ProgressChartNode = {
  isLatest: boolean;
  key: string;
  value: number;
  x: number;
  y: number;
};

/** One unbroken run of answered readings. A skipped check-in starts a new one. */
export type ProgressChartSegment = {
  areaPath: string | null;
  key: string;
  linePath: string;
};

export type ProgressChartTick = {
  label: string;
  widthPx: number;
  x: number;
};

export type ProgressChartIdealSpan = {
  endX: number;
  startX: number;
};

export type ProgressChartValueLabelPlacement = (typeof VALUE_LABEL_PLACEMENTS)[number];

export type ProgressChartPillPlacement = {
  x: "center" | "left" | "right";
  y: "above" | "below";
};

export type ProgressChartGeometry = {
  ceilY: number;
  floorY: number;
  idealSpan: ProgressChartIdealSpan;
  idealY: number | null;
  nodes: ProgressChartNode[];
  segments: ProgressChartSegment[];
  /** Check-ins that were submitted with this question unanswered. */
  skippedMarkers: { key: string; x: number }[];
  ticks: ProgressChartTick[];
};

type Slot = {
  epoch: number;
  isBaseline: boolean;
  key: string;
  value: number | null;
};

type AnsweredSlot = Slot & { value: number };

const toEpoch = (iso: string) => {
  const epoch = new Date(iso).getTime();

  return Number.isFinite(epoch) ? epoch : null;
};

const round = (value: number) => {
  return Math.round(value * 100) / 100;
};

const isAnsweredSlot = (slot: Slot): slot is AnsweredSlot => {
  return slot.value !== null;
};

/** Points keep the payload order: the contract guarantees ascending `recordedAt`. */
const buildSlots = (chart: ProgressChart): Slot[] => {
  const slots: Slot[] = [];
  const baselineEpoch = chart.baseline ? toEpoch(chart.baseline.recordedAt) : null;

  if (chart.baseline && baselineEpoch !== null) {
    slots.push({
      epoch: baselineEpoch,
      isBaseline: true,
      key: `${chart.key}-baseline`,
      value: chart.baseline.value,
    });
  }

  chart.points.forEach((point, index) => {
    const epoch = toEpoch(point.recordedAt);

    if (epoch === null) {
      return;
    }

    slots.push({
      epoch,
      isBaseline: false,
      key: `${chart.key}-point-${index}-${point.checkInId}`,
      value: point.value,
    });
  });

  return slots;
};

const toSmoothPath = (nodes: ProgressChartNode[], ceilY: number, floorY: number) => {
  /** A cubic stays inside its own control points, so clamping them keeps the curve on the scale. */
  const clampY = (value: number) => {
    return round(Math.min(Math.max(value, ceilY), floorY));
  };

  const [first] = nodes;
  let path = `M ${first.x} ${first.y}`;

  for (let index = 0; index < nodes.length - 1; index += 1) {
    const previous = nodes[index - 1] ?? nodes[index];
    const start = nodes[index];
    const end = nodes[index + 1];
    const next = nodes[index + 2] ?? end;

    const firstControlX = round(start.x + ((end.x - previous.x) / 6) * SMOOTHING);
    const firstControlY = clampY(start.y + ((end.y - previous.y) / 6) * SMOOTHING);
    const secondControlX = round(end.x - ((next.x - start.x) / 6) * SMOOTHING);
    const secondControlY = clampY(end.y - ((next.y - start.y) / 6) * SMOOTHING);

    path += ` C ${firstControlX} ${firstControlY}, ${secondControlX} ${secondControlY}, ${end.x} ${end.y}`;
  }

  return path;
};

const toSegment = (
  nodes: ProgressChartNode[],
  ceilY: number,
  floorY: number,
): ProgressChartSegment => {
  const linePath = toSmoothPath(nodes, ceilY, floorY);
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  const isLoneReading = nodes.length === 1;
  const areaPath = isLoneReading
    ? null
    : `${linePath} L ${last.x} ${floorY} L ${first.x} ${floorY} Z`;

  return { areaPath, key: first.key, linePath };
};

/** Every coordinate is a percentage of the plot box: 0–100 on both axes. */
export const buildProgressChartGeometry = (chart: ProgressChart): ProgressChartGeometry => {
  const floorY = 100 - Y_INSET_BOTTOM;
  const ceilY = Y_INSET_TOP;
  const { max, min } = chart.scale;
  const scaleSpan = max - min;

  const toY = (value: number) => {
    if (scaleSpan <= 0) {
      return (ceilY + floorY) / 2;
    }

    const clamped = Math.min(Math.max(value, min), max);

    return round(ceilY + ((max - clamped) / scaleSpan) * (floorY - ceilY));
  };

  const slots = buildSlots(chart);
  const originEpoch = slots.length > 0 ? slots[0].epoch : null;

  if (originEpoch === null) {
    return {
      ceilY,
      floorY,
      idealSpan: { endX: 100, startX: 0 },
      idealY: chart.idealLine ? toY(chart.idealLine.idealScore) : null,
      nodes: [],
      segments: [],
      skippedMarkers: [],
      ticks: [],
    };
  }

  const epochs = slots.map((slot) => {
    return slot.epoch;
  });
  /* Nothing guarantees the baseline predates every check-in, so the axis spans the readings too. */
  const startEpoch = Math.min(...epochs, originEpoch);
  const endEpoch = Math.max(...epochs, originEpoch + AXIS_MIN_SPAN_DAYS * MS_PER_DAY);

  const toX = (epoch: number) => {
    return round(X_INSET + ((epoch - startEpoch) / (endEpoch - startEpoch)) * (100 - X_INSET * 2));
  };

  /* Always to the axis edge: stopping at `horizonAt` cut it a third of the way across after profiling. */
  const idealSpan: ProgressChartIdealSpan = {
    endX: toX(endEpoch),
    startX: toX(originEpoch),
  };

  /* Matched by position, not `checkInId`: a duplicated id would mark two nodes as latest. */
  const answeredSlots = slots.filter((slot) => {
    return isAnsweredSlot(slot) && !slot.isBaseline;
  });
  const hasLatestReading = chart.latest !== null && chart.latest.value !== null;
  const latestKey =
    hasLatestReading && answeredSlots.length > 0
      ? answeredSlots[answeredSlots.length - 1].key
      : null;

  const nodes: ProgressChartNode[] = [];
  const segments: ProgressChartSegment[] = [];
  const skippedMarkers: { key: string; x: number }[] = [];
  let run: ProgressChartNode[] = [];

  const closeRun = () => {
    if (run.length > 0) {
      segments.push(toSegment(run, ceilY, floorY));
      run = [];
    }
  };

  slots.forEach((slot) => {
    if (!isAnsweredSlot(slot)) {
      skippedMarkers.push({ key: slot.key, x: toX(slot.epoch) });
      closeRun();

      return;
    }

    const node: ProgressChartNode = {
      isLatest: slot.key === latestKey,
      key: slot.key,
      value: slot.value,
      x: toX(slot.epoch),
      y: toY(slot.value),
    };

    nodes.push(node);
    run.push(node);
  });

  closeRun();

  const ticks: ProgressChartTick[] = [];

  AXIS_TICKS.forEach((tick) => {
    const x = toX(originEpoch + tick.day * MS_PER_DAY);

    if (x > 100) {
      return;
    }

    ticks.push({ label: tick.label, widthPx: tick.widthPx, x });
  });

  return {
    ceilY,
    floorY,
    idealSpan,
    idealY: chart.idealLine ? toY(chart.idealLine.idealScore) : null,
    nodes,
    segments,
    skippedMarkers,
    ticks,
  };
};

/** Centred labels overlap when their gap is under half of each width; the later one drops. */
export const filterVisibleTicks = (ticks: ProgressChartTick[], plotWidth: number) => {
  if (plotWidth <= 0) {
    return ticks;
  }

  const visible: ProgressChartTick[] = [];

  ticks.forEach((tick) => {
    const previous = visible[visible.length - 1];

    if (previous !== undefined) {
      const gapPx = ((tick.x - previous.x) / 100) * plotWidth;
      const requiredGapPx = previous.widthPx / 2 + tick.widthPx / 2 + TICK_LABEL_PADDING_PX;

      if (gapPx < requiredGapPx) {
        return;
      }
    }

    visible.push(tick);
  });

  return visible;
};

const valueLabelWidthPx = (value: number) => {
  return [...formatProgressScore(value)].reduce((width, character) => {
    return width + (character === "." ? VALUE_LABEL_POINT_PX : VALUE_LABEL_DIGIT_PX);
  }, 0);
};

const isValueLabelCrowded = (
  previous: ProgressChartNode | undefined,
  node: ProgressChartNode,
  plotWidth: number,
) => {
  if (previous === undefined) {
    return false;
  }

  const gapPx = ((node.x - previous.x) / 100) * plotWidth;
  const requiredGapPx =
    valueLabelWidthPx(previous.value) / 2 +
    valueLabelWidthPx(node.value) / 2 +
    VALUE_LABEL_PADDING_PX;

  return gapPx < requiredGapPx;
};

/** Keys of the nodes whose value label fits. The latest keeps its label even when crowded. */
export const filterVisibleValueLabels = (nodes: ProgressChartNode[], plotWidth: number) => {
  if (plotWidth <= 0) {
    return new Set(
      nodes.map((node) => {
        return node.key;
      }),
    );
  }

  const kept: ProgressChartNode[] = [];

  nodes.forEach((node) => {
    const previous = kept[kept.length - 1];
    const isCrowded = isValueLabelCrowded(previous, node, plotWidth);
    const mustEvictPrevious = isCrowded && node.isLatest;

    if (isCrowded && !mustEvictPrevious) {
      return;
    }

    if (mustEvictPrevious) {
      kept.pop();
    }

    kept.push(node);
  });

  return new Set(
    kept.map((node) => {
      return node.key;
    }),
  );
};

/** Pixels inside the plot box, measured from its top-left corner. */
type LabelBox = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};

const overlaps = (one: LabelBox, other: LabelBox) => {
  return (
    one.left < other.right &&
    other.left < one.right &&
    one.top < other.bottom &&
    other.top < one.bottom
  );
};

/** Which way the "You Are Here" pill is anchored on its dot. Edge-aware, never neighbor-aware. */
export const resolvePillPlacement = (node: ProgressChartNode): ProgressChartPillPlacement => {
  const y = node.y <= PILL_BELOW_MAX_Y ? "below" : "above";

  if (node.x > PILL_RIGHT_ANCHOR_X) {
    return { x: "right", y };
  }

  return { x: node.x < PILL_LEFT_ANCHOR_X ? "left" : "center", y };
};

const dotBox = (node: ProgressChartNode, plotWidth: number, plotHeight: number): LabelBox => {
  const size = node.isLatest ? LATEST_DOT_BOX_PX : DOT_BOX_PX;
  const centerX = (node.x / 100) * plotWidth;
  const centerY = (node.y / 100) * plotHeight;

  return {
    bottom: centerY + size / 2,
    left: centerX - size / 2,
    right: centerX + size / 2,
    top: centerY - size / 2,
  };
};

/* The pill is anchored on the marker box, which is the dot: `left-0` starts at the dot's edge. */
const pillLeftPx = (dot: LabelBox, x: ProgressChartPillPlacement["x"]) => {
  if (x === "left") {
    return dot.left;
  }

  if (x === "right") {
    return dot.right - PILL_WIDTH_PX;
  }

  return (dot.left + dot.right) / 2 - PILL_WIDTH_PX / 2;
};

const pillBox = (node: ProgressChartNode, plotWidth: number, plotHeight: number): LabelBox => {
  const dot = dotBox(node, plotWidth, plotHeight);
  const placement = resolvePillPlacement(node);
  const top =
    placement.y === "below"
      ? dot.bottom + PILL_BELOW_GAP_PX
      : dot.top - PILL_ABOVE_GAP_PX - PILL_HEIGHT_PX;
  const left = pillLeftPx(dot, placement.x);

  return { bottom: top + PILL_HEIGHT_PX, left, right: left + PILL_WIDTH_PX, top };
};

const valueLabelBox = (
  node: ProgressChartNode,
  placement: ProgressChartValueLabelPlacement,
  plotWidth: number,
  plotHeight: number,
): LabelBox => {
  const dot = dotBox(node, plotWidth, plotHeight);
  const width = valueLabelWidthPx(node.value);
  const centerX = (dot.left + dot.right) / 2;
  const centerY = (dot.top + dot.bottom) / 2;
  const top = centerY - VALUE_LABEL_HEIGHT_PX / 2;

  if (placement === "left") {
    const right = dot.left - VALUE_LABEL_GAP_PX;

    return { bottom: top + VALUE_LABEL_HEIGHT_PX, left: right - width, right, top };
  }

  if (placement === "right") {
    const left = dot.right + VALUE_LABEL_GAP_PX;

    return { bottom: top + VALUE_LABEL_HEIGHT_PX, left, right: left + width, top };
  }

  if (placement === "below") {
    const below = dot.bottom + VALUE_LABEL_GAP_PX;

    return {
      bottom: below + VALUE_LABEL_HEIGHT_PX,
      left: centerX - width / 2,
      right: centerX + width / 2,
      top: below,
    };
  }

  const bottom = dot.top - VALUE_LABEL_GAP_PX;

  return {
    bottom,
    left: centerX - width / 2,
    right: centerX + width / 2,
    top: bottom - VALUE_LABEL_HEIGHT_PX,
  };
};

/**
 * Where each surviving value label sits, keyed by node. A label absent from the map is not drawn.
 *
 * Readings minutes apart share a column, so the one label the width filter keeps would be drawn
 * over a neighboring dot, and the pill covers whatever shares its band. Both are only visible
 * once the second axis is taken into account, which the width filter alone never does.
 */
export const resolveValueLabelPlacements = (
  nodes: ProgressChartNode[],
  plotWidth: number,
  plotHeight: number,
) => {
  const placements = new Map<string, ProgressChartValueLabelPlacement>();
  const labelled = filterVisibleValueLabels(nodes, plotWidth);

  if (plotWidth <= 0 || plotHeight <= 0) {
    nodes.forEach((node) => {
      if (labelled.has(node.key)) {
        placements.set(node.key, "above");
      }
    });

    return placements;
  }

  const occupied = nodes.map((node) => {
    return dotBox(node, plotWidth, plotHeight);
  });

  nodes.forEach((node) => {
    if (node.isLatest) {
      occupied.push(pillBox(node, plotWidth, plotHeight));
    }
  });

  /* The latest reading is placed first, so it keeps the usual spot whenever one is still free. */
  const ordered = nodes
    .filter((node) => {
      return labelled.has(node.key);
    })
    .sort((left, right) => {
      return Number(right.isLatest) - Number(left.isLatest);
    });

  ordered.forEach((node) => {
    const placement = VALUE_LABEL_PLACEMENTS.find((candidate) => {
      const box = valueLabelBox(node, candidate, plotWidth, plotHeight);

      return !occupied.some((taken) => {
        return overlaps(box, taken);
      });
    });

    if (placement === undefined) {
      return;
    }

    occupied.push(valueLabelBox(node, placement, plotWidth, plotHeight));
    placements.set(node.key, placement);
  });

  return placements;
};
